import React, { useState } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI, s_PL } from '../../shared/styles';
import { useStorage } from '../../shared/hooks';
import { playSound } from '../../shared/soundEngine';
import { MOOD_OPTIONS, MOOD_TAGS_NL, MOOD_TAGS_EN } from '../../shared/constants';

const SLEEP_TIPS = {
  nl: {
    great:  { icon: "🌟", title: "Top slaap!", msg: "Je bent goed uitgerust. Hou dit ritme vast!" },
    good:   { icon: "😴", title: "Goede nacht", msg: "Probeer 30 minuten eerder naar bed voor nóg betere rust." },
    ok:     { icon: "🌙", title: "Matige nacht", msg: "Vermijd schermen een uur voor het slapengaan." },
    poor:   { icon: "🌿", title: "Slechte slaap", msg: "Probeer een wind-down routine: dimmen, geen cafeïne na 15:00." },
  },
  en: {
    great:  { icon: "🌟", title: "Great sleep!", msg: "You're well-rested. Keep this rhythm going!" },
    good:   { icon: "😴", title: "Good night", msg: "Try getting to bed 30 min earlier for even better rest." },
    ok:     { icon: "🌙", title: "Average night", msg: "Avoid screens an hour before bed to improve sleep quality." },
    poor:   { icon: "🌿", title: "Poor sleep", msg: "Try a wind-down routine: dim lights, no caffeine after 3 PM." },
  },
};

const MOOD_TIPS = {
  nl: {
    great:   { icon: "😄", title: "Geweldig!", msg: "Houd dit gevoel vast en deel je energie met anderen." },
    good:    { icon: "🙂", title: "Goed gevoel!", msg: "Vier de kleine overwinningen — het telt." },
    neutral: { icon: "🌊", title: "Neutraal is oké", msg: "Probeer box breathing: 4s inademen, 4s vasthouden, 4s uitademen." },
    low:     { icon: "🌿", title: "Even opladen", msg: "Een wandeling van 10 minuten buiten vermindert angst met 21%." },
    bad:     { icon: "💙", title: "Zware dag", msg: "Wees lief voor jezelf — rust is ook herstel." },
  },
  en: {
    great:   { icon: "😄", title: "Amazing!", msg: "Hold onto this feeling and share your energy with others." },
    good:    { icon: "🙂", title: "Good vibes!", msg: "Celebrate the small wins — they all count." },
    neutral: { icon: "🌊", title: "Neutral is fine", msg: "Try box breathing: 4s in → hold 4s → out 4s. Resets your system." },
    low:     { icon: "🌿", title: "Time to recharge", msg: "10 minutes outside, no phone, reduces anxiety markers by 21%." },
    bad:     { icon: "💙", title: "Tough day", msg: "Be kind to yourself — rest is recovery too. Tomorrow is a new day." },
  },
};

function getSleepTipKey(score) {
  if (score >= 8) return 'great';
  if (score >= 6) return 'good';
  if (score >= 4) return 'ok';
  return 'poor';
}

function getMoodTipKey(moodId) {
  return moodId || 'neutral';
}

function FeedbackBanner({ data, accent, onDismiss }) {
  if (!data) return null;
  return (
    <div style={{ background: `${accent}15`, border: `1.5px solid ${accent}44`, borderRadius: 16, padding: '14px 16px', marginTop: 12, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 24, flexShrink: 0 }}>{data.icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ color: accent, fontWeight: 700, fontSize: 13, marginBottom: 3 }}>{data.title}</div>
        <div style={{ color: C.silver2, fontSize: 12, lineHeight: 1.5 }}>{data.msg}</div>
      </div>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 16, cursor: 'pointer', padding: 0, flexShrink: 0, lineHeight: 1 }}>✕</button>
    </div>
  );
}

function SleepMoodScreen({lang}) {
  const nl=lang==="nl";
  const [tab,setTab]=useState("today");
  const [bedtime,setBedtime]=useState("23:00");
  const [waketime,setWaketime]=useState("07:00");
  const [sleepQual,setSleepQual]=useState(3);
  const [selMood,setSelMood]=useState(null);
  const [moodNote,setMoodNote]=useState("");
  const [moodTags,setMoodTags]=useState([]);
  const [sleepFeedback,setSleepFeedback]=useState(null);
  const [moodFeedback,setMoodFeedback]=useState(null);
  const [sleepLog,setSleepLog]=useStorage("sl_sleep_log",[]);
  const [moodLog,setMoodLog]=useStorage("sl_mood_log",[]);

  const todayKey=new Date().toISOString().slice(0,10);
  const todaySleep=sleepLog.find(e=>e.date===todayKey)||null;
  const calcDuration=(bed,wake)=>{const[bh,bm]=bed.split(":").map(Number),[wh,wm]=wake.split(":").map(Number);let mins=(wh*60+wm)-(bh*60+bm);if(mins<0)mins+=1440;return mins;};
  const durationMins=calcDuration(bedtime,waketime);
  const sleepScore=Math.min(10,Math.round((Math.min(durationMins,480)/480)*7+sleepQual*0.6));

  const saveSleep=()=>{
    setSleepLog(p=>[{date:todayKey,bedtime,waketime,durationMins,quality:sleepQual,score:sleepScore,ts:Date.now()},...p.filter(e=>e.date!==todayKey)]);
    const tipKey = getSleepTipKey(sleepScore);
    const tips = nl ? SLEEP_TIPS.nl : SLEEP_TIPS.en;
    setSleepFeedback(tips[tipKey]);
  };

  const saveMood=()=>{
    if(!selMood)return;
    const moodOpt=MOOD_OPTIONS.find(m=>m.id===selMood);
    setMoodLog(p=>[{date:todayKey,mood:selMood,score:moodOpt?.score??3,tags:moodTags,note:moodNote,ts:Date.now()},...p.filter(e=>e.date!==todayKey)]);
    const tipKey = getMoodTipKey(selMood);
    const tips = nl ? MOOD_TIPS.nl : MOOD_TIPS.en;
    setMoodFeedback(tips[tipKey] || tips['neutral']);
    setSelMood(null);setMoodTags([]);setMoodNote("");
  };

  return (
    <div className="fadeUp">
      <style>{CSS}</style>
      <div style={{display:"flex",background:_T.bg4,borderRadius:14,padding:4,marginBottom:16,gap:2}}>
        {[{id:"today",icon:"📝",label:nl?"Vandaag":"Today"},{id:"history",icon:"📊",label:nl?"Historie":"History"}].map(t=>(<button key={t.id} onClick={()=>{playSound('tab');setTab(t.id);}} style={{flex:1,background:tab===t.id?`linear-gradient(135deg,${_T.grad1},${_T.grad2})`:"transparent",border:"none",borderRadius:10,padding:"9px 4px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2}}><span style={{fontSize:16}}>{t.icon}</span><span style={{fontSize:9,color:tab===t.id?"#0a1220":C.silver,fontWeight:tab===t.id?700:400}}>{t.label}</span></button>))}
      </div>
      {tab==="today"&&<div>
        <div style={s_GC({marginBottom:14})}>
          <div style={{fontSize:13,color:C.teal,letterSpacing:3,fontWeight:600,marginBottom:12}}>😴 {nl?"SLAAP LOGGEN":"LOG SLEEP"}</div>
          {todaySleep && !sleepFeedback ? (
            <div>
              <div style={{background:`${C.teal}15`,border:`1.5px solid ${C.teal}44`,borderRadius:14,padding:"14px 16px",marginBottom:12}}>
                <div style={{color:C.teal,fontWeight:700,fontSize:13,marginBottom:6}}>✅ {nl?"Al gelogd vandaag":"Already logged today"}</div>
                <div style={{display:"flex",gap:12,alignItems:"center"}}>
                  <div style={{flex:1}}>
                    <div style={{color:C.silver,fontSize:12}}>🌙 {todaySleep.bedtime} → ☀️ {todaySleep.waketime}</div>
                    <div style={{color:C.silver,fontSize:12}}>{Math.floor(todaySleep.durationMins/60)}h {todaySleep.durationMins%60}m · {"⭐".repeat(todaySleep.quality||0)}</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:22,color:todaySleep.score>=7?C.teal:_T.accent,fontWeight:700}}>{todaySleep.score}/10</div>
                    <div style={{fontSize:9,color:C.silver}}>Score</div>
                  </div>
                </div>
              </div>
              <button style={s_PB({width:"100%",opacity:0.6,fontSize:12})} onClick={()=>setSleepFeedback(null)}>
                {nl?"Opnieuw loggen":"Log again"}
              </button>
            </div>
          ) : (
            <div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                <div><div style={{fontSize:11,color:C.silver,marginBottom:5}}>🌙 {nl?"Bedtijd":"Bedtime"}</div><input type="time" value={bedtime} onChange={e=>setBedtime(e.target.value)} style={s_TI({padding:"10px 12px"})}/></div>
                <div><div style={{fontSize:11,color:C.silver,marginBottom:5}}>☀️ {nl?"Wektijd":"Wake"}</div><input type="time" value={waketime} onChange={e=>setWaketime(e.target.value)} style={s_TI({padding:"10px 12px"})}/></div>
              </div>
              <div style={{display:"flex",gap:10,marginBottom:12}}>
                <div style={{flex:1,background:_T.bg4,borderRadius:12,padding:"10px",textAlign:"center"}}><div style={{fontSize:18,color:durationMins>=420?C.teal:_T.accent,fontWeight:700}}>{Math.floor(durationMins/60)}u{durationMins%60}m</div><div style={{fontSize:9,color:C.silver}}>{nl?"Duur":"Duration"}</div></div>
                <div style={{flex:1,background:_T.bg4,borderRadius:12,padding:"10px",textAlign:"center"}}><div style={{fontSize:18,color:sleepScore>=7?C.teal:_T.accent,fontWeight:700}}>{sleepScore}/10</div><div style={{fontSize:9,color:C.silver}}>Score</div></div>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:12}}>{[1,2,3,4,5].map(s=><button key={s} onClick={()=>setSleepQual(s)} style={{background:"none",border:"none",cursor:"pointer",fontSize:24,opacity:s<=sleepQual?1:0.2}}>⭐</button>)}</div>
              <button style={s_PB({width:"100%"})} onClick={saveSleep}>{nl?"Slaap opslaan":"Save sleep"}</button>
              <FeedbackBanner data={sleepFeedback} accent={C.teal} onDismiss={()=>setSleepFeedback(null)}/>
            </div>
          )}
        </div>
        <div style={s_GC()}>
          <div style={{fontSize:13,color:C.purple,letterSpacing:3,fontWeight:600,marginBottom:12}}>💭 {nl?"STEMMING LOGGEN":"LOG MOOD"}</div>
          <div style={{display:"flex",gap:6,justifyContent:"space-between",marginBottom:12}}>{MOOD_OPTIONS.map(m=>(<button key={m.id} onClick={()=>setSelMood(m.id)} style={{flex:1,background:selMood===m.id?`${m.color}25`:_T.bg4,border:`2px solid ${selMood===m.id?m.color:_T.cardBorderBot}`,borderRadius:12,padding:"8px 2px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}><span style={{fontSize:22}}>{m.emoji}</span><span style={{fontSize:8,color:selMood===m.id?m.color:C.silver}}>{nl?m.label:m.labelEN}</span></button>))}</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:10}}>{(nl?MOOD_TAGS_NL:MOOD_TAGS_EN).map(tag=>(<button key={tag} style={s_PL(moodTags.includes(tag),C.purple)} onClick={()=>setMoodTags(p=>p.includes(tag)?p.filter(t=>t!==tag):[...p,tag])}>{tag}</button>))}</div>
          <textarea value={moodNote} onChange={e=>setMoodNote(e.target.value)} placeholder={nl?"Hoe was je dag?":"How was your day?"} style={s_TI({minHeight:60,resize:"none",marginBottom:10,padding:"10px 12px"})}/>
          <button style={s_PB({width:"100%",opacity:selMood?1:0.4})} onClick={saveMood} disabled={!selMood}>{nl?"Stemming opslaan":"Save mood"}</button>
          <FeedbackBanner data={moodFeedback} accent={C.purple} onDismiss={()=>setMoodFeedback(null)}/>
        </div>
      </div>}
      {tab==="history"&&<div>
        {sleepLog.length===0&&moodLog.length===0
          ? <div style={{...s_GC({textAlign:"center",padding:28}),color:C.silver,fontSize:13}}>{nl?"Log je slaap en stemming om history te zien.":"Log your sleep and mood to see history."}</div>
          : (() => {
              const allDates = [...new Set([...sleepLog.map(e=>e.date),...moodLog.map(e=>e.date)])].sort((a,b)=>b.localeCompare(a)).slice(0,14);
              return allDates.map(date => {
                const s = sleepLog.find(e=>e.date===date);
                const m = moodLog.find(e=>e.date===date);
                const moodOpt = m ? MOOD_OPTIONS.find(o=>o.id===m.mood) : null;
                const durStr = s
                  ? (s.bedtime && s.waketime
                      ? `🌙 ${s.bedtime} → ☀️ ${s.waketime} · ${Math.floor(s.durationMins/60)}h${s.durationMins%60}m`
                      : `😴 ${s.hours ?? Math.floor((s.durationMins||0)/60)}h ${"⭐".repeat(s.quality||0)}`)
                  : null;
                return (
                  <div key={date} style={s_GC({padding:14,marginBottom:8})}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div style={{color:C.white,fontSize:13,fontWeight:600}}>{date}</div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        {s && <div style={{color:C.teal,fontWeight:700,fontSize:13}}>{s.score}/10</div>}
                        {moodOpt && <div style={{fontSize:18}}>{moodOpt.emoji}</div>}
                      </div>
                    </div>
                    {durStr && <div style={{color:C.silver,fontSize:12,marginBottom:moodOpt?4:0}}>{durStr}</div>}
                    {moodOpt && <div style={{color:moodOpt.color,fontSize:12,fontWeight:500}}>{nl?moodOpt.label:moodOpt.labelEN}{m.note?` · "${m.note}"`:""}  {m.tags?.length?m.tags.join(", "):""}</div>}
                  </div>
                );
              });
            })()
        }
      </div>}
    </div>
  );
}

export default SleepMoodScreen;
